import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles, Check, XCircle, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const ACTION_LABELS = {
  create_ideas: "Générer des idées de publications",
  generate_calendar: "Générer un calendrier éditorial brouillon",
  create_publication: "Créer une publication brouillon",
};

export default function Assistant() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  const context = () => {
    const p = location.pathname;
    return {
      page: p,
      client_id: p.match(/^\/clients\/([\w-]+)/)?.[1],
      publication_id: p.match(/^\/publications\/([\w-]+)/)?.[1],
    };
  };

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    const history = messages.map((m) => ({ role: m.role, text: m.text }));
    setMessages((ms) => [...ms, { role: "user", text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/assistant/chat", { message: text, history, context: context() });
      setMessages((ms) => [...ms, {
        role: "assistant", text: data.reply || "…",
        action: data.action, actionStatus: data.action ? "pending" : null,
      }]);
    } catch (e) {
      toast.error(apiError(e));
      setMessages((ms) => [...ms, { role: "assistant", text: "Une erreur est survenue — réessayez." }]);
    } finally { setLoading(false); }
  };

  const confirmAction = async (idx, action) => {
    const setStatus = (s) => setMessages((ms) => ms.map((m, i) => (i === idx ? { ...m, actionStatus: s } : m)));
    setStatus("running");
    try {
      if (action.type === "create_ideas") {
        const { data } = await api.post(`/ai/clients/${action.client_id}/ideas/generate`, {
          count: action.count || 8, month: action.month || "ce mois-ci", description: action.description || "",
        });
        toast.success(`${data.length} idées générées — onglet Idées IA`);
      } else if (action.type === "generate_calendar") {
        await api.post(`/ai/clients/${action.client_id}/calendar/generate`, {
          month: action.month, count: action.count || 8,
        });
        toast.success("Calendrier brouillon généré — visible dans le calendrier du client");
      } else if (action.type === "create_publication") {
        const { data } = await api.post("/publications", {
          client_id: action.client_id, title: action.title, date: action.date,
          time: action.time || "18:00", format: action.format || "post",
          platform: action.platform || "instagram", objective: action.objective || "engagement",
          status: "a_creer",
        });
        toast.success("Publication créée en brouillon");
        navigate(`/publications/${data.id}`);
      } else {
        throw new Error("Action inconnue");
      }
      setStatus("done");
    } catch (e) {
      setStatus("error");
      toast.error(apiError(e));
    }
  };

  return (
    <>
      <button
        data-testid="assistant-fab"
        onClick={() => setOpen(!open)}
        title="Assistant IA"
        className="fixed bottom-5 left-5 z-50 w-12 h-12 rounded-full bg-neutral-900 hover:bg-neutral-800 text-white shadow-lg flex items-center justify-center transition-all hover:scale-105"
      >
        {open ? <X className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
      </button>

      {open && (
        <div className="fixed bottom-20 left-5 z-50 w-96 max-w-[calc(100vw-2.5rem)] h-[60vh] bg-white border border-neutral-200 rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-fade-up"
          data-testid="assistant-panel">
          <div className="px-4 py-3 border-b border-neutral-100 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-neutral-700" />
            <p className="font-heading font-bold text-sm flex-1">Assistant DGA</p>
            <span className="text-[10px] text-neutral-400">Conseille ou crée après confirmation</span>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="assistant-messages">
            {messages.length === 0 && (
              <div className="text-sm text-neutral-400 space-y-2 pt-2" data-testid="assistant-empty">
                <p className="font-medium text-neutral-600">Bonjour Aurélie, que puis-je faire pour vous ?</p>
                <p className="text-xs">Exemples : « Donne-moi 3 angles reel pour ce client », « Génère le calendrier de septembre de Brin de Beauté », « Rédige une réponse à un client qui trouve le devis cher ».</p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"} data-testid={`assistant-msg-${i}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-line ${
                  m.role === "user" ? "bg-neutral-900 text-white" : "bg-neutral-100 text-neutral-800"}`}>
                  {m.text}
                  {m.action && (
                    <div className="mt-2.5 bg-white border border-neutral-200 rounded-xl p-3 text-neutral-800" data-testid={`assistant-action-${i}`}>
                      <p className="text-xs font-semibold flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5" /> {ACTION_LABELS[m.action.type] || m.action.type}
                      </p>
                      <p className="text-[11px] text-neutral-500 mt-1">
                        {m.action.title || ""}{m.action.count ? `${m.action.count} contenus` : ""}{m.action.month ? ` · ${m.action.month}` : ""}
                      </p>
                      {m.actionStatus === "pending" && (
                        <div className="flex gap-2 mt-2">
                          <Button size="sm" data-testid={`assistant-action-confirm-${i}`} onClick={() => confirmAction(i, m.action)}
                            className="bg-neutral-900 hover:bg-neutral-800 text-white gap-1 h-7 text-xs">
                            <Check className="w-3 h-3" /> Confirmer
                          </Button>
                          <Button size="sm" variant="outline" data-testid={`assistant-action-cancel-${i}`}
                            onClick={() => setMessages((ms) => ms.map((x, j) => (j === i ? { ...x, actionStatus: "cancelled" } : x)))}
                            className="gap-1 h-7 text-xs">
                            <XCircle className="w-3 h-3" /> Annuler
                          </Button>
                        </div>
                      )}
                      {m.actionStatus === "running" && <p className="text-xs text-neutral-500 mt-2 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Exécution…</p>}
                      {m.actionStatus === "done" && <p className="text-xs text-green-600 font-medium mt-2">Fait ✓</p>}
                      {m.actionStatus === "cancelled" && <p className="text-xs text-neutral-400 mt-2">Annulé</p>}
                      {m.actionStatus === "error" && <p className="text-xs text-red-600 mt-2">Échec — réessayez</p>}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-neutral-100 rounded-2xl px-3.5 py-2.5 text-sm text-neutral-500 flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Réflexion…
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="border-t border-neutral-100 p-3 flex gap-2">
            <input
              data-testid="assistant-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Votre message… (Entrée pour envoyer)"
              className="flex-1 text-sm border border-neutral-200 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-neutral-400"
            />
            <button data-testid="assistant-send" onClick={send} disabled={loading || !input.trim()}
              className="bg-neutral-900 hover:bg-neutral-800 text-white rounded-md px-3 transition-colors disabled:opacity-50">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
